import wstETHIcon from "./assets/images/wstETH.svg"
import sfrxETHIcon from "./assets/images/sfrxETH.svg"
import SLSDIcon from "./assets/images/sLSD.svg"

export type Lexicon = {
  term: string;
  description?: string;
  link?: string;
  iconUrl?: string
};

export type CollateralLexicon = Lexicon & {
  key: string;
  name: string;
}

export const BORROW_FEE: Lexicon = {
  term: "Borrowing Fee",
  description:
    "Every time you draw SLSD from your Position, a one-off borrowing fee is charged on the drawn amount and added to your debt. Please note that the borrowing fee is variable (and determined algorithmically) and has a minimum value of 0.5% under normal operation. The fee is 0% during Recovery Mode."
};

export const TVL: Lexicon = {
  term: "Total Collateral",
  description:
    "The total value of sfrxETH locked as collateral in the system, given in sfrxETH and USD."
};

export const STAKED_PSY: Lexicon = {
  term: "Staked PSY",
  description: "The total amount of PSY that is staked for earning fee revenue."
};

export const TCR: Lexicon = {
  term: "Total Collateral Ratio",
  description:
    "The ratio of the Dollar value of the entire system collateral at the current sfrxETH:USD price, to the entire system debt."
};

export const RECOVERY_MODE: Lexicon = {
  term: "Recovery Mode",
  description:
    "Recovery Mode is activated when the Total Collateral Ratio (TCR) falls below 150%. When active, your Trove can be liquidated if its collateral ratio is below the TCR. The maximum collateral you can lose from liquidation is capped at 110% of your Trove's debt. Operations are also restricted that would negatively impact the TCR."
};

export const STABILITY_POOL_SLSD: Lexicon = {
  term: "SLSD in Stability Pool",
  description:
    "The total SLSD currently held in the Stability Pool, expressed as an amount and a fraction of the SLSD supply."
};

export const KICKBACK_RATE: Lexicon = {
  term: "Kickback Rate",
  description:
    "A rate between 0 and 100% set by the Frontend Operator that determines the fraction of PSY that will be paid out as a kickback to the Stability Providers using the frontend."
};

export const ETH: Lexicon = {
  term: "ETH"
};

export const SLSD: Lexicon = {
  term: "SLSD",
  iconUrl: SLSDIcon
};

export const PSY: Lexicon = {
  term: "PSY"
};

export const WSTETH: CollateralLexicon = {
  key: "wstEthToken",
  name: "Wrapped stETH",
  term: "wstETH",
  iconUrl: wstETHIcon
};

export const SFRXETH: CollateralLexicon = {
  key: "sfrxEthToken",
  name: "Staked Frax ETH",
  term: "sfrxETH",
  iconUrl: sfrxETHIcon
};

export const TROVES: Lexicon = {
  term: "Troves",
  description: "The total number of active Troves in the system."
};

export const COL_PRICE: Lexicon = {
  term: "Collateral Price",
  description: "The current price of the collateral in USD."
};

export const LIQUIDATION_PRICE: Lexicon = {
  term: "Your Liquidation Price",
  description: "When the collateral price falls under the Liquidation Price (USD) you will get liquidated."
};

export const SLSD_SUPPLY: Lexicon = {
  term: "SLSD supply",
  description: "The total SLSD minted by the PSY Protocol."
};

export const TOTAL_COLLATERAL_RATIO: Lexicon = {
  term: "Total Collateral Ratio",
  description: "The sum of the collateral of all positions expressed in USD, divided by the debt of all positions expressed in SLSD. The System aims to be over 150% TCR at all times."
};

export const MY_COLLATERAL_RATIO: Lexicon = {
  term: "Your Collateral Ratio",
  description: "The collateral ratio of your position. The minimum CR is 110% while your position can be subject to liquidation if it is under 150%."
};

export const LIQUIDATION_RESERVE: (value: string) => Lexicon = (value) => ({
  term: "Liquidation Reserve Fee",
  description: `When you open a position and draw a loan, ${value} SLSD is set aside as a way to compensate gas costs for the transaction sender in the event your Position being liquidated. The Liquidation Reserve is fully refundable if your Position is not liquidated, and is given back to you when you close your Position by repaying your debt. The Liquidation Reserve counts as debt and is taken into account for the calculation of a Position's collateral ratio, slightly increasing the actual collateral requirements.`
});

export const TOTAL_DEBT: Lexicon = {
  term: "Total Debt",
  description: "The total debt you have to pay back, in order to get your collateral back. In consists of your loan + the liquidation reserve fee + the borrowing fee. When paying back on normal mode, you don’t have to pay back the liquidation reserve fee."
};

export const SEVEN_DAY_APR: Lexicon = {
  term: "7 day APR",
  description: "The displayed reward is average of the last 7 days. The actual APR varies depending on the distribution of PSY and liquidation happening on the protocol."
};

export const ONE_DAY_APR: Lexicon = {
  term: "24h APR",
  description: "The displayed reward is average of the last 24 hours. The actual APR varies depending on the distribution of PSY and liquidation happening on the protocol."
};

export const CollateralLexicons = [SFRXETH, WSTETH]
